import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../utils/crmApi';
import { useCRM } from '../../contexts/CRMContext';

const AcceptInvite = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setUser, setCurrentAgencyById } = useCRM?.() || {};
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [requiresPassword, setRequiresPassword] = useState(true);
    const token = searchParams.get('token') || '';

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing invitation token');
            setLoading(false);
            return;
        }
        // We optimistically show password form. Some invites may not require password if already registered.
        setRequiresPassword(true);
        setLoading(false);
    }, [token]);

    const handleAccept = async (e) => {
        e.preventDefault();
        setError('');

        if (requiresPassword) {
            if (!password || password.length < 8) {
                setError('Password must be at least 8 characters');
                return;
            }
            if (password !== confirm) {
                setError('Passwords do not match');
                return;
            }
        }

        try {
            setLoading(true);
            const res = await authAPI.verifyEmail({
                token,
                password: requiresPassword ? password : undefined,
            });
            if (res.data?.success && res.data?.token) {
                localStorage.setItem('crm_token', res.data.token);
                // Choose agency context for client users
                const memberships = res.data?.user?.agencyMemberships || [];
                const firstMembership = memberships[0];
                const agencyId = firstMembership?.agencyId?._id || firstMembership?.agencyId;
                if (agencyId) {
                    localStorage.setItem('current_agency_id', String(agencyId));
                    try {
                        await authAPI.switchAgency(String(agencyId));
                    } catch (e) {
                        // non-blocking
                    }
                    if (typeof setCurrentAgencyById === 'function') {
                        try { setCurrentAgencyById(String(agencyId)); } catch (_) {}
                    }
                }
                if (typeof setUser === 'function' && res.data.user) {
                    try { setUser(res.data.user); } catch (_) {}
                }
                navigate('/client-portal', { replace: true });
            } else {
                setError(res.data?.message || 'Failed to accept invitation');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to accept invitation');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-muted">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Processing invitation…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted px-4">
            <div className="w-full max-w-md bg-card p-6 rounded-xl shadow-sm border">
                <h1 className="text-xl font-semibold text-foreground mb-2">Accept Invitation</h1>
                <p className="text-sm text-muted-foreground mb-4">Set your password to activate your account.</p>

                {error && (
                    <div className="mb-4 text-sm text-red-600">{error}</div>
                )}

                <form onSubmit={handleAccept} className="space-y-4">
                    {requiresPassword && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Password</label>
                                <input
                                    type="password"
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                                    placeholder="Enter a strong password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Confirm Password</label>
                                <input
                                    type="password"
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                                    placeholder="Re-enter your password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    required
                                    minLength={8}
                                />
                            </div>
                        </>
                    )}

                    <button
                        type="submit"
                        className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60"
                        disabled={loading}
                    >
                        {loading ? 'Submitting…' : 'Activate and Continue'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AcceptInvite;


